import React, { useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import classnames from 'classnames'
import { Button, Modal, Typography } from 'antd'
import { CheckCircleFilled, FileProtectOutlined } from '@ant-design/icons'
import { FE_ROUTE, RECAPTCHA_SITE_KEY, TEXT_ERROR_CODE, TEXT_UI_CONSENT } from 'config'
import { UserService } from 'services'
import { ErrorHandlerUtils, MiscUtils, ReCaptchaUtils } from 'utils'
import { setCurrentUserAction } from 'redux/auth/actions'
import ConsentCheckboxes, { consentStyles, toConsentsPayload } from '../ConsentCheckboxes'
import styles from './style.module.scss'

const EMPTY_CONSENTS = { required: false, optional: false }

// the consent modal must not cover the documents it links to, nor the logout page
const NO_CONSENT_MODAL_PATHS = [FE_ROUTE.MISC.TOS, FE_ROUTE.MISC.PRIVACY, FE_ROUTE.AUTH.LOGOUT]

// shown on every page until the user agrees the current policy version
const ModalAgreeTerm = ({ currentUser }) => {
  const intl = useIntl()
  const history = useHistory()
  const { pathname } = useLocation()
  const recaptchaRef = useRef()
  const [consents, setConsents] = useState(EMPTY_CONSENTS)
  const [loading, setLoading] = useState(false)
  const [isSuccessShown, setIsSuccessShown] = useState(false)
  const policyVersion = currentUser?.policyVersion

  // in-app navigation like the header link: a full reload of /auth/* makes InitialCheck
  // redirect a logged-in user to the home page before the Logout page runs
  const logout = () => history.push(FE_ROUTE.AUTH.LOGOUT)

  const submit = async () => {
    setLoading(true)
    let recaptchaToken
    try {
      recaptchaToken = await ReCaptchaUtils.getValue(recaptchaRef)
    } catch (error) {
      setLoading(false)
      ErrorHandlerUtils.http(error, intl)
      return
    }
    UserService.agreeConsent({
      consents: toConsentsPayload(consents, policyVersion),
      policyVersion,
      recaptchaToken,
    })
      .then((user) => {
        ReCaptchaUtils.reset(recaptchaRef)
        setLoading(false)
        setConsents(EMPTY_CONSENTS)
        setIsSuccessShown(true)
        MiscUtils.dispatchReduxAction(setCurrentUserAction(user))
      })
      .catch((error) => {
        ReCaptchaUtils.reset(recaptchaRef)
        setLoading(false)
        // a new policy version was activated: reload user to get the new wording
        if (error?.response?.data?.error === TEXT_ERROR_CODE.POLICY_VERSION_CHANGED) {
          setConsents(EMPTY_CONSENTS)
          UserService.sync()
            .then((user) => MiscUtils.dispatchReduxAction(setCurrentUserAction(user)))
            .catch((e) => ErrorHandlerUtils.http(e))
        }
        ErrorHandlerUtils.http(error, intl)
      })
  }

  return (
    <>
      <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={recaptchaRef} />

      <Modal
        visible={
          !!currentUser?.needAgreeTerm &&
          !isSuccessShown &&
          !NO_CONSENT_MODAL_PATHS.includes(pathname)
        }
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={560}
      >
        <div className={styles.body}>
          <div className={styles.header}>
            <FileProtectOutlined className={styles.icon} />
            <Typography.Title level={4}>
              <FormattedMessage id={TEXT_UI_CONSENT.MODAL.TITLE} />
            </Typography.Title>
            <Typography.Text type="secondary">
              <FormattedMessage id={TEXT_UI_CONSENT.MODAL.DESCRIPTION} />
            </Typography.Text>
          </div>
          <ConsentCheckboxes
            policyVersion={policyVersion}
            value={consents}
            onChange={setConsents}
            showOptionalTag
            className={consentStyles.box}
          />
          <div className={styles.actions}>
            <Button
              size="large"
              className={classnames(styles.action, 'ant-btn-cancel')}
              disabled={loading}
              onClick={logout}
            >
              <FormattedMessage id={TEXT_UI_CONSENT.MODAL.LOGOUT} />
            </Button>
            <Button
              type="primary"
              size="large"
              loading={loading}
              disabled={consents.required !== true || loading}
              className={classnames(styles.action, {
                'ant-btn-primary-disabled': consents.required !== true || loading,
              })}
              onClick={submit}
            >
              <FormattedMessage id={TEXT_UI_CONSENT.MODAL.SUBMIT} />
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        visible={isSuccessShown}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={420}
      >
        <div className={styles.body}>
          <div className={styles.header}>
            <CheckCircleFilled className={styles['success-icon']} />
            <Typography.Title level={4}>
              <FormattedMessage id={TEXT_UI_CONSENT.SUCCESS.TITLE} />
            </Typography.Title>
            <Typography.Text type="secondary">
              <FormattedMessage id={TEXT_UI_CONSENT.SUCCESS.DESCRIPTION} />
            </Typography.Text>
          </div>
          <Button
            type="primary"
            size="large"
            className={styles.submit}
            onClick={() => setIsSuccessShown(false)}
          >
            <FormattedMessage id={TEXT_UI_CONSENT.SUCCESS.CONTINUE} />
          </Button>
        </div>
      </Modal>
    </>
  )
}

const mapStateToProps = ({ authReducer: { currentUser } }) => ({ currentUser })
export default connect(mapStateToProps)(ModalAgreeTerm)
